import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useTheme } from "@/contexts/ThemeContext";
import { apiService, SubscriptionPlan, SubscriptionAnalytics } from "@/services/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Plus, Edit, Trash2, Save, CheckCircle, Zap, Crown, Star, RefreshCw } from "lucide-react";
import { SubscriptionAnalytics as SubscriptionAnalyticsComponent } from "../components/SubscriptionAnalytics";

export default function SubscriptionPlans() {
  const { currentTheme } = useTheme();
  const [activeTab, setActiveTab] = useState("analytics");
  const [subscriptions, setSubscriptions] = useState<SubscriptionPlan[]>([]);
  const [analyticsData, setAnalyticsData] = useState<SubscriptionAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load subscriptions
  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAllSubscriptions();
      if (response.type === 1) {
        setSubscriptions(response.data);
      } else {
        toast.error(response.message || "Failed to load subscriptions");
      }
    } catch (error) {
      toast.error("Failed to load subscriptions");
      console.error("Error loading subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load analytics
  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await apiService.getSubscriptionAnalytics();
      if (response.type === 1) {
        setAnalyticsData(response.data);
      } else {
        toast.error(response.message || "Failed to load analytics");
      }
    } catch (error) {
      toast.error("Failed to load analytics");
      console.error("Error loading analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    void loadSubscriptions();
    void loadAnalytics();
  }, []);

  const handleCreatePlan = () => {
    setIsCreatingPlan(true);
    setEditingPlan({
      _id: "",
      name: "",
      discountPriceTag: 0,
      price: 0,
      maxGigs: 0,
      benefits: [],
      createdAt: "",
      updatedAt: "",
    });
  };

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setEditingPlan({ ...plan });
    setIsCreatingPlan(false);
  };

  const handleSavePlan = async () => {
    if (!editingPlan) return;

    if (editingPlan.name.trim() === "") {
      toast.error("Plan name is required");
      return;
    }

    if (editingPlan.price < 0) {
      toast.error("Price must be a positive number");
      return;
    }

    if (editingPlan.maxGigs < 0) {
      toast.error("Max gigs must be a positive number");
      return;
    }

    try {
      setIsSubmitting(true);

      if (isCreatingPlan) {
        const response = await apiService.createSubscription({
          name: editingPlan.name,
          discountPriceTag: editingPlan.discountPriceTag,
          price: editingPlan.price,
          maxGigs: editingPlan.maxGigs,
          benefits: editingPlan.benefits,
        });

        if (response.type === 1) {
          toast.success("Subscription plan created successfully");
          await loadSubscriptions();
        } else {
          toast.error(response.message || "Failed to create plan");
        }
      } else {
        const response = await apiService.updateSubscription(editingPlan._id, {
          name: editingPlan.name,
          discountPriceTag: editingPlan.discountPriceTag,
          price: editingPlan.price,
          maxGigs: editingPlan.maxGigs,
          benefits: editingPlan.benefits,
        });

        if (response.type === 1) {
          toast.success("Subscription plan updated successfully");
          await loadSubscriptions();
        } else {
          toast.error(response.message || "Failed to update plan");
        }
      }

      setEditingPlan(null);
      setIsCreatingPlan(false);
    } catch (error) {
      toast.error("Failed to save plan");
      console.error("Error saving plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (!confirm("Are you sure you want to delete this subscription plan?")) return;

    try {
      const response = await apiService.deleteSubscription(planId);
      if (response.type === 1) {
        toast.success("Subscription plan deleted successfully");
        await loadSubscriptions();
      } else {
        toast.error(response.message || "Failed to delete plan");
      }
    } catch (error) {
      toast.error("Failed to delete plan");
      console.error("Error deleting plan:", error);
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("MMM DD, YYYY");
  };

  const getPriorityColor = (price: number) => {
    if (price === 0) return "bg-gray-100 text-gray-800";
    if (price < 50) return "bg-blue-100 text-blue-800";
    return "bg-purple-100 text-purple-800";
  };

  const getPriorityIcon = (price: number) => {
    if (price === 0) return <Star className="w-4 h-4" />;
    if (price < 50) return <Zap className="w-4 h-4" />;
    return <Crown className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Subscription Plans</h1>
          <p className="text-muted-foreground">Manage subscription plans and pricing</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => {
              void loadSubscriptions();
              void loadAnalytics();
            }}
            variant="outline"
            size="sm"
            disabled={loading || analyticsLoading}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${loading || analyticsLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button onClick={handleCreatePlan} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Create Plan</span>
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="plans">Subscription Plans</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-4">
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading analytics...
            </div>
          ) : analyticsData ? (
            <SubscriptionAnalyticsComponent data={analyticsData} />
          ) : (
            <div className="text-center py-8">
              <Crown className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No analytics data available</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="plans" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading subscription plans...
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-8">
              <Crown className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No subscription plans found</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {subscriptions.map(plan => (
                <Card key={plan._id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getPriorityIcon(plan.price)}
                        <Badge className={getPriorityColor(plan.price)}>${plan.price}/month</Badge>
                      </div>
                    </div>
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <CardDescription>Up to {plan.maxGigs} gigs per month</CardDescription>
                    <div className="flex items-baseline space-x-1">
                      <span className="text-3xl font-bold">${plan.price}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <ul className="space-y-2">
                      {plan.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="text-xs text-muted-foreground pt-2">
                      Created: {formatDate(plan.createdAt)}
                    </div>
                    <div className="flex space-x-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditPlan(plan)}
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleDeletePlan(plan._id)}
                        className="flex-1"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Plan Edit Modal */}
      {editingPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4">
            <CardHeader>
              <CardTitle>{isCreatingPlan ? "Create New Plan" : "Edit Plan"}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Plan Name</Label>
                  <Input
                    id="name"
                    value={editingPlan.name}
                    onChange={e => {
                      setEditingPlan({ ...editingPlan, name: e.target.value });
                    }}
                    placeholder="Enter plan name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Price ($)</Label>
                  <Input
                    id="price"
                    type="number"
                    value={editingPlan.price}
                    onChange={e => {
                      setEditingPlan({
                        ...editingPlan,
                        price: parseFloat(e.target.value) || 0,
                      });
                    }}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="discountPriceTag">Discount Price Tag (%)</Label>
                  <Input
                    id="discountPriceTag"
                    type="number"
                    value={editingPlan.discountPriceTag}
                    onChange={e => {
                      setEditingPlan({
                        ...editingPlan,
                        discountPriceTag: parseFloat(e.target.value) || 0,
                      });
                    }}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxGigs">Max Gigs</Label>
                  <Input
                    id="maxGigs"
                    type="number"
                    value={editingPlan.maxGigs}
                    onChange={e => {
                      setEditingPlan({
                        ...editingPlan,
                        maxGigs: parseInt(e.target.value) || 0,
                      });
                    }}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Benefits (one per line)</Label>
                <Textarea
                  value={editingPlan.benefits.join("\n")}
                  onChange={e => {
                    setEditingPlan({
                      ...editingPlan,
                      benefits: e.target.value.split("\n").filter(f => f.trim()),
                    });
                  }}
                  className="h-24"
                  placeholder="Enter benefits, one per line"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingPlan(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePlan}
                  disabled={isSubmitting}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Saving..." : "Save Plan"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
